import { type Component, For, Show, createSignal, createEffect, onMount } from 'solid-js';
import { Portal } from 'solid-js/web';
import { gameStore } from '@/stores/gameStore';
import { sanitizeText } from '@/utils/validation';
import { useTranslation } from '@/i18n';

interface TileModalProps {
  tileNumber: number;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (digit: string) => void;
}

// Detect iOS
const isIOS = () => {
  const result = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  console.log('[TileModal] isIOS:', result, 'UA:', navigator.userAgent.substring(0, 50));
  return result;
};

const TileModal: Component<TileModalProps> = (props) => {
  const { t } = useTranslation();
  const [answerText, setAnswerText] = createSignal('');
  const [isSubmitting, setIsSubmitting] = createSignal(false);
  const [result, setResult] = createSignal<{ success: boolean; message: string; digit?: string } | null>(null);
  let inputRef: HTMLInputElement | undefined;
  let modalRef: HTMLDivElement | undefined;
  let overlayRef: HTMLDivElement | undefined;

  // Scroll and focus when modal opens
  createEffect(() => {
    if (props.isOpen) {
      // Scroll to top - aggressive for iOS
      window.scrollTo(0, 0);
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;

      // Auto-focus only on Android - iOS handles focus natively on tap
      if (!isIOS()) {
        setTimeout(() => {
          inputRef?.focus();
        }, 200);
      }
    }
  });

  // Scroll modal into view when result appears
  createEffect(() => {
    if (result() && modalRef) {
      modalRef.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    if (!answerText().trim() || isSubmitting()) return;

    setIsSubmitting(true);
    setResult(null);

    try {
      const response = await gameStore.submitAnswer(
        props.tileNumber,
        sanitizeText(answerText())
      );

      if (response.status === 'correct') {
        setResult({
          success: true,
          message: t('notifications.answerCorrect'),
          digit: response.assigned_digit
        });

        setTimeout(() => {
          props.onSuccess(response.assigned_digit || '?');
          props.onClose();
          setAnswerText('');
          setResult(null);
        }, 2000);
      } else {
        setResult({
          success: false,
          message: t('tileModal.wrongAnswer'),
        });
      }
    } catch (error) {
      setResult({
        success: false,
        message: t('tileModal.connectionError')
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting()) {
      setAnswerText('');
      setResult(null);
      props.onClose();
    }
  };

  // Handle overlay click - close only when clicking backdrop
  const handleOverlayClick = (e: MouseEvent) => {
    if (e.target === overlayRef) {
      handleClose();
    }
  };

  // ============ ANDROID VERSION (Original Tailwind) ============
  const AndroidModal = () => (
    <div
      ref={overlayRef}
      class="fixed inset-0 z-50 flex items-center justify-center px-4 py-8 bg-black/80 overflow-y-auto"
      onClick={handleOverlayClick}
    >
      <div
        ref={modalRef}
        class="relative w-full max-w-sm bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl border-2 border-cyan-500/30 shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div class="relative px-5 pt-4 pb-3">
          <div class="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500" />

          <button
            type="button"
            onClick={handleClose}
            class="absolute top-3 right-3 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white/70 text-xl"
          >
            ✕
          </button>

          <div class="text-center pr-8">
            <div class="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-purple-500/20 border border-white/10 mb-3">
              <span class="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
                {props.tileNumber}
              </span>
            </div>
            <h3 class="text-lg font-bold text-white">{t('tileModal.questionNumber')} {props.tileNumber}</h3>
            <p class="text-sm text-white/50 mt-0.5">{t('tileModal.enterAnswer')}</p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} class="px-5 pb-6">
          <div class="mb-4">
            <input
              ref={inputRef}
              type="text"
              placeholder={t('tileModal.answerPlaceholder')}
              value={answerText()}
              onInput={(e) => setAnswerText(e.currentTarget.value)}
              disabled={isSubmitting() || result()?.success}
              class="w-full px-4 py-4 bg-white/5 border-2 border-white/10 rounded-2xl text-white placeholder-white/30 focus:outline-none focus:border-cyan-500/50 text-center text-lg font-medium"
              autocomplete="off"
            />
          </div>

          {/* Result Message */}
          <Show when={result()}>
            <div class={`mb-4 p-4 rounded-2xl text-center ${
              result()!.success ? 'bg-emerald-500/20 border-2 border-emerald-500/40' : 'bg-red-500/20 border-2 border-red-500/40'
            }`}>
              <div class="text-4xl mb-2">{result()!.success ? '🎉' : '😔'}</div>
              <div class={`font-bold text-lg ${result()!.success ? 'text-emerald-400' : 'text-red-400'}`}>
                {result()!.success ? t('tileModal.correct') : t('tileModal.incorrect')}
              </div>
              <div class={`text-sm mt-1 ${result()!.success ? 'text-emerald-300/80' : 'text-red-300/80'}`}>
                {result()!.message}
              </div>
              <Show when={result()!.success && result()!.digit}>
                <div class="mt-3 py-2 px-4 bg-white/10 rounded-xl inline-block">
                  <span class="text-white/60 text-sm">{t('tileModal.digitForCode')}: </span>
                  <span class="text-3xl font-bold text-cyan-400">{result()!.digit}</span>
                </div>
              </Show>
            </div>
          </Show>

          {/* Submit Button */}
          <Show when={!result()?.success}>
            <button
              type="submit"
              disabled={!answerText().trim() || isSubmitting()}
              class="w-full py-4 rounded-2xl font-bold text-lg bg-gradient-to-r from-cyan-500 to-purple-500 text-white disabled:opacity-40"
            >
              {isSubmitting() ? t('tileModal.checking') : t('tileModal.checkAnswer')}
            </button>
          </Show>
        </form>
      </div>
    </div>
  );

  // ============ iOS VERSION - 100% CLEAN CODE FOR SAFARI ============
  const IOSModal = () => {
    const [closing, setClosing] = createSignal(false);

    // Auto-scroll to top when modal opens on iOS
    onMount(() => {
      window.scrollTo(0, 0);
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    });

    const closeModal = () => {
      if (closing()) return;
      setClosing(true);
      handleClose();
    };

    return (
      <div style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.85)",
        "z-index": 99999,
        display: "flex",
        "align-items": "flex-start",
        "justify-content": "center",
        "padding-top": "60px",
        "padding-left": "16px",
        "padding-right": "16px",
        "overflow-y": "auto"
      }}>
          {/* Modal card */}
          <div style={{
            width: "100%",
            "max-width": "360px",
            background: "#1e293b",
            "border-radius": "20px",
            overflow: "hidden"
          }}>
            {/* Top gradient bar */}
            <div style={{
              height: "4px",
              background: "linear-gradient(90deg, #06b6d4, #8b5cf6, #ec4899)"
            }} />

            {/* Header with close button */}
            <div style={{
              padding: "16px",
              display: "flex",
              "justify-content": "space-between",
              "align-items": "flex-start"
            }}>
              {/* Title area */}
              <div style={{ flex: 1, "padding-right": "12px" }}>
                <div style={{
                  display: "inline-flex",
                  "align-items": "center",
                  "justify-content": "center",
                  width: "48px",
                  height: "48px",
                  background: "linear-gradient(135deg, #06b6d4, #8b5cf6)",
                  "border-radius": "12px",
                  "margin-bottom": "8px"
                }}>
                  <span style={{ color: "white", "font-size": "20px", "font-weight": "bold" }}>
                    {props.tileNumber}
                  </span>
                </div>
                <div style={{ color: "white", "font-size": "16px", "font-weight": "600" }}>
                  {t('tileModal.questionNumber')} {props.tileNumber}
                </div>
                <div style={{ color: "#64748b", "font-size": "13px", "margin-top": "2px" }}>
                  {t('tileModal.enterAnswer')}
                </div>
              </div>

              {/* Close button - iOS needs onTouchEnd */}
              <button
                type="button"
                onTouchEnd={(e) => { e.preventDefault(); closeModal(); }}
                onClick={() => closeModal()}
                style={{
                  width: "40px",
                  height: "40px",
                  "min-width": "40px",
                  background: "#ef4444",
                  border: "none",
                  "border-radius": "50%",
                  color: "white",
                  "font-size": "20px",
                  "line-height": "1",
                  cursor: "pointer"
                }}
              >×</button>
            </div>

            {/* Form */}
            <div style={{ padding: "0 16px 20px 16px" }}>
              <form onSubmit={handleSubmit}>
                {/* Input field */}
                <input
                  ref={inputRef}
                  type="text"
                  enterkeyhint="send"
                  autocomplete="off"
                  autocapitalize="off"
                  autocorrect="off"
                  spellcheck={false}
                  placeholder={t('tileModal.answerPlaceholder')}
                  value={answerText()}
                  onInput={(e) => setAnswerText(e.currentTarget.value)}
                  disabled={isSubmitting() || result()?.success}
                  style={{
                    width: "100%",
                    padding: "14px",
                    "font-size": "17px",
                    "text-align": "center",
                    background: "#0f172a",
                    border: "2px solid #334155",
                    "border-radius": "12px",
                    color: "white",
                    "-webkit-appearance": "none"
                  }}
                />

                {/* Result */}
                <Show when={result()}>
                  <div style={{
                    "margin-top": "16px",
                    padding: "16px",
                    background: result()!.success ? "#065f46" : "#7f1d1d",
                    "border-radius": "12px",
                    "text-align": "center",
                    color: "white"
                  }}>
                    <div style={{ "font-size": "36px" }}>{result()!.success ? '🎉' : '😔'}</div>
                    <div style={{ "font-size": "16px", "font-weight": "bold", "margin-top": "8px" }}>
                      {result()!.success ? t('tileModal.correct') : t('tileModal.incorrect')}
                    </div>
                    <div style={{ "font-size": "14px", "margin-top": "4px", opacity: 0.9 }}>
                      {result()!.message}
                    </div>
                    <Show when={result()!.success && result()!.digit}>
                      <div style={{
                        "margin-top": "12px",
                        padding: "10px",
                        background: "rgba(255,255,255,0.15)",
                        "border-radius": "8px"
                      }}>
                        <div style={{ "font-size": "12px", opacity: 0.8 }}>{t('tileModal.digitForCode')}</div>
                        <div style={{ "font-size": "28px", "font-weight": "bold" }}>{result()!.digit}</div>
                      </div>
                    </Show>
                  </div>
                </Show>

                {/* Submit button */}
                <Show when={!result()?.success}>
                  <button
                    type="submit"
                    disabled={!answerText().trim() || isSubmitting()}
                    style={{
                      width: "100%",
                      "margin-top": "16px",
                      padding: "14px",
                      "font-size": "16px",
                      "font-weight": "600",
                      background: (!answerText().trim() || isSubmitting()) ? "#334155" : "#0891b2",
                      color: (!answerText().trim() || isSubmitting()) ? "#64748b" : "white",
                      border: "none",
                      "border-radius": "12px",
                      cursor: (!answerText().trim() || isSubmitting()) ? "default" : "pointer"
                    }}
                  >
                    {isSubmitting() ? t('tileModal.checking') : t('tileModal.checkAnswer')}
                  </button>
                </Show>
              </form>
            </div>
          </div>
      </div>
    );
  };

  return (
    <Show when={props.isOpen}>
      {isIOS() ? (
        <Portal mount={document.body}>
          <IOSModal />
        </Portal>
      ) : (
        <Portal mount={document.body}>
          <AndroidModal />
        </Portal>
      )}
    </Show>
  );
};

const GameProgress: Component = () => {
  const { t } = useTranslation();
  const [selectedTile, setSelectedTile] = createSignal<number | null>(null);
  const [successAnimation, setSuccessAnimation] = createSignal<number | null>(null);

  // Check if game is active
  const isGameActive = () => gameStore.isGameActive();
  const gameStatus = () => gameStore.gameState.game?.status || 'planned';

  // Check if team has already finished (new architecture)
  const isTeamFinished = () => {
    if (gameStore.gameState.architecture === 'new' && gameStore.gameState.participation) {
      return gameStore.gameState.participation.is_finished;
    }
    return gameStore.gameState.team?.is_finished || false;
  };

  const getAnswerStatus = (tileNumber: number) => {
    // Support both legacy (answer_id) and new (answer/tile_number) architecture
    const progress = gameStore.gameState.progress.find(p =>
      p.tile_number === tileNumber ||
      p.answer_id === tileNumber ||
      (p as any).answer === gameStore.gameState.answers.find(a => a.tile_number === tileNumber)?.id
    );
    return progress?.status || 'pending';
  };

  const getAssignedDigit = (tileNumber: number) => {
    const answer = gameStore.gameState.answers.find(a => a.tile_number === tileNumber);
    return answer?.assigned_digit || '?';
  };

  const handleTileClick = (tileNumber: number) => {
    // Block if game is not active or team has already finished
    if (!isGameActive() || isTeamFinished()) return;

    const status = getAnswerStatus(tileNumber);
    if (status !== 'correct') {
      setSelectedTile(tileNumber);
    }
  };

  const handleSuccess = (digit: string) => {
    if (selectedTile()) {
      setSuccessAnimation(selectedTile());
      setTimeout(() => setSuccessAnimation(null), 1000);
    }
  };

  const completedAnswers = () => gameStore.gameState.answers.filter(answer =>
    getAnswerStatus(answer.tile_number) === 'correct'
  ).length;

  const totalAnswers = () => gameStore.gameState.answers.length;

  const getStatusMessage = () => {
    // Team finished takes priority
    if (isTeamFinished()) {
      return { icon: '🎉', title: t('gameProgress.gameCompleted'), subtitle: t('gameProgress.congratsWaitResults') };
    }
    const status = gameStatus();
    switch (status) {
      case 'planned':
      case 'planowana':
        return { icon: '⏳', title: t('gameProgress.gameNotStartedYet'), subtitle: t('gameProgress.waitForOperator') };
      case 'finished':
      case 'zakonczona':
        return { icon: '🏁', title: t('gameProgress.gameEnded'), subtitle: t('gameProgress.thankYou') };
      case 'paused':
      case 'wstrzymana':
        return { icon: '⏸️', title: t('gameProgress.gamePaused'), subtitle: t('gameProgress.waitForResume') };
      default:
        return null;
    }
  };

  return (
    <div class="space-y-4">
      {/* Progress Header - isolation prevents stacking context issues with modal */}
      <div class="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 border border-white/10 p-4" style={{ isolation: "isolate" }}>
        <div class="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500" />

        <div class="flex items-center justify-between mb-3">
          <h3 class="text-base font-bold text-white flex items-center gap-2">
            <span class="text-xl">🎯</span>
            {t('gameProgress.yourProgress')}
          </h3>
          <div class="px-3 py-1.5 rounded-full bg-white/10 border border-white/10">
            <span class="text-xl font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
              {completedAnswers()}/{totalAnswers()}
            </span>
          </div>
        </div>

        {/* Progress Bar - no shimmer animation on iOS (causes stacking context issues) */}
        <div class="relative h-2.5 bg-white/10 rounded-full overflow-hidden">
          <div
            class="absolute inset-y-0 left-0 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 rounded-full transition-all duration-700 ease-out"
            style={{ width: `${totalAnswers() > 0 ? (completedAnswers() / totalAnswers()) * 100 : 0}%` }}
          />
          {/* Shimmer only on Android - iOS has stacking context issues with transform animations */}
          <Show when={!isIOS()}>
            <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
          </Show>
        </div>
      </div>

      {/* Game Not Active or Team Finished Overlay */}
      <Show when={(!isGameActive() || isTeamFinished()) && totalAnswers() > 0}>
        <div class={`relative rounded-2xl p-5 text-center ${
          isTeamFinished()
            ? 'bg-gradient-to-br from-emerald-500/10 to-green-500/10 border-2 border-emerald-500/30'
            : 'bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-2 border-amber-500/30'
        }`}>
          <div class="text-4xl mb-2">{getStatusMessage()?.icon || '⏳'}</div>
          <h4 class={`text-lg font-bold mb-1 ${isTeamFinished() ? 'text-emerald-400' : 'text-amber-400'}`}>
            {getStatusMessage()?.title || t('gameProgress.gameInactive')}
          </h4>
          <p class={`text-sm ${isTeamFinished() ? 'text-emerald-300/70' : 'text-amber-300/70'}`}>
            {getStatusMessage()?.subtitle || t('gameProgress.tilesUnlocked')}
          </p>
        </div>
      </Show>

      {/* Tiles Grid */}
      <div class="grid grid-cols-2 gap-3">
        <For each={gameStore.gameState.answers.sort((a, b) => a.tile_number - b.tile_number)}>
          {(answer) => {
            const status = () => getAnswerStatus(answer.tile_number);
            const isCorrect = () => status() === 'correct';
            const isAnimating = () => successAnimation() === answer.tile_number;
            const isDisabled = () => !isGameActive() || isCorrect() || isTeamFinished();

            return (
              <button
                onClick={() => handleTileClick(answer.tile_number)}
                disabled={isDisabled()}
                class={`
                  relative aspect-square rounded-2xl border-2 transition-all duration-300 overflow-hidden min-h-[140px]
                  ${isCorrect()
                    ? 'bg-gradient-to-br from-emerald-500/20 to-green-600/20 border-emerald-500/40'
                    : isDisabled()
                      ? 'bg-gradient-to-br from-slate-800/60 to-slate-700/60 border-white/5 opacity-60'
                      : 'bg-gradient-to-br from-slate-800/90 to-slate-700/90 border-white/10 active:border-cyan-500/50 active:scale-[0.97]'
                  }
                  ${isAnimating() ? 'animate-success-pulse' : ''}
                `}
              >
                {/* Locked overlay when game not active or team has finished */}
                <Show when={(!isGameActive() || isTeamFinished()) && !isCorrect()}>
                  <div class="absolute inset-0 bg-slate-900/40 flex items-center justify-center z-10">
                    <span class="text-3xl opacity-50">{isTeamFinished() ? '✅' : '🔒'}</span>
                  </div>
                </Show>

                {/* Glow effect for correct */}
                <Show when={isCorrect()}>
                  <div class="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-transparent" />
                  <div class="absolute top-2 right-2">
                    <span class="text-emerald-400 text-2xl">✓</span>
                  </div>
                </Show>

                {/* Content */}
                <div class="absolute inset-0 flex flex-col items-center justify-center p-3">
                  <Show when={!isCorrect()} fallback={
                    // Correct state - show digit
                    <>
                      <div class="text-emerald-400/80 text-[10px] font-semibold uppercase tracking-wider mb-0.5">
                        {t('gameProgress.questionLabel')} {answer.tile_number}
                      </div>
                      <div class="w-8 h-0.5 bg-emerald-500/30 rounded-full mb-2" />
                      <div class="text-emerald-400/80 text-[10px] font-semibold uppercase tracking-wider mb-0.5">
                        {t('gameProgress.digitForCode')}
                      </div>
                      <div class="text-5xl font-black bg-gradient-to-br from-emerald-300 to-green-400 bg-clip-text text-transparent">
                        {getAssignedDigit(answer.tile_number)}
                      </div>
                    </>
                  }>
                    {/* Pending state */}
                    <div class="text-white/40 text-[10px] font-semibold uppercase tracking-wider mb-1">
                      {t('gameProgress.questionLabel')}
                    </div>
                    <div class="text-5xl font-black bg-gradient-to-br from-white/90 to-white/50 bg-clip-text text-transparent leading-none">
                      {answer.tile_number}
                    </div>
                    <Show when={isGameActive()}>
                      <div class="mt-2 px-2.5 py-1 rounded-full bg-white/5 border border-white/10">
                        <span class="text-[10px] text-white/50 font-medium">{t('gameProgress.tapToOpen')}</span>
                      </div>
                    </Show>
                  </Show>
                </div>
              </button>
            );
          }}
        </For>
      </div>

      {/* Empty state */}
      <Show when={gameStore.gameState.answers.length === 0}>
        <div class="text-center py-10">
          <div class="text-5xl mb-3 animate-bounce">🎮</div>
          <h4 class="text-lg font-bold text-white mb-1">{t('gameProgress.noPuzzles')}</h4>
          <p class="text-sm text-white/50">{t('gameProgress.waitForGameStart')}</p>
        </div>
      </Show>

      {/* Answer Modal */}
      <TileModal
        tileNumber={selectedTile() || 1}
        isOpen={selectedTile() !== null}
        onClose={() => setSelectedTile(null)}
        onSuccess={handleSuccess}
      />

      {/* Custom animations */}
      <style>{`
        @keyframes modal-in {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }

        @keyframes slide-down {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes slide-up {
          from { opacity: 0; transform: translateY(100px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        @keyframes success-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.03); }
        }

        .animate-modal-in {
          animation: modal-in 0.2s ease-out;
        }

        .animate-slide-down {
          animation: slide-down 0.25s ease-out;
        }

        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }

        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }

        .animate-shimmer {
          animation: shimmer 2s infinite;
        }

        .animate-success-pulse {
          animation: success-pulse 0.4s ease-in-out;
        }

        .safe-area-bottom {
          padding-bottom: env(safe-area-inset-bottom, 0);
        }
      `}</style>
    </div>
  );
};

export default GameProgress;
